
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import CalendarV2 from './pages/CalendarV2';
import DayListReservations from './pages/DayListReservations';
import VehicleList from './pages/VehicleList';
import ReservationList from './pages/ReservationList';
import Dashboard from './pages/Dashboard';
import StockCheck from './pages/StockCheck';
import BulkMail from './pages/BulkMail';
import PaymentCheck from './pages/PaymentCheck';
import AiChatReview from './pages/AiChatReview';
import AiActionLog from './pages/AiActionLog';
const MobilePage = React.lazy(() => import('./pages/mobile/MobilePage'));
import { AuthProvider, useAuth, LoginForm } from './components/AuthContext';
import { setAuthToken as setDispatchAuthToken } from './api/dispatchClient';
import { ChangeManagerProvider } from './context/ChangeManagerContext';
import ChangeHistoryPanel from './components/ChangeHistoryPanel';
import ChangeHistoryIcon from './components/ChangeHistoryIcon';
import StaffSelector from './components/StaffSelector';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import LineChatBoard from './components/LineChatBoard';
import StaffChatManager from './components/StaffChatManager';
import RecentSummaryFloat from './components/RecentSummaryFloat';
import AgentFloat from './components/AgentFloat';
import { ReservationDataProvider } from './context/ReservationDataContext';
import ChatIcon from '@mui/icons-material/Chat';
import {
    AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Box,
  Drawer, List, ListItem, ListItemText, ListItemIcon, Divider, useMediaQuery, useTheme, CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LogoutIcon from '@mui/icons-material/Logout';
import SessionExpiryWarning from './components/SessionExpiryWarning';
import PusherToast from './components/PusherToast';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChangeManagerProvider>
          <AppContent />
          <ChangeHistoryPanel />
        </ChangeManagerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

import { useGlobalReservationSync } from './hooks/useGlobalReservationSync';
import { useReservationStatuses } from './hooks/useReservationStatuses';
import { setStatusCache } from './lib/statusMapper';

function AppContent() {
  const headerRef = useRef(null);
  const { user, loading, isAuthenticated, useMockAuth, logout, softLogin, softLoggedOut, renderKey, dispatch: authDispatch } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // テスト用: ?logouttest=1 で3秒後にトークンを無効化（1回のみ）
  const logoutTestFiredRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logouttest') === '1' && isAuthenticated && !softLoggedOut && !logoutTestFiredRef.current) {
      const timer = setTimeout(() => {
        console.log('[App] logouttest=1: invalidating token after 3s');
        logoutTestFiredRef.current = true;
        // URLからパラメータを除去
        const url = new URL(window.location.href);
        url.searchParams.delete('logouttest');
        window.history.replaceState({}, '', url.toString());
        // トークン無効化
        localStorage.removeItem('authToken');
        setDispatchAuthToken(null);
        authDispatch({ type: 'SOFT_LOGOUT' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, softLoggedOut, authDispatch]);

  // Initialize global reservation sync (only when authenticated)
  const { pollingEnabled, togglePolling } = useGlobalReservationSync(user && isAuthenticated);

  // ChangeHistoryPanel にポーリング制御を公開
  useEffect(() => {
    window.__pollingControl = { pollingEnabled, togglePolling };
    return () => { delete window.__pollingControl; };
  }, [pollingEnabled, togglePolling]);

  // Pusher 接続（認証時のみ）
  useEffect(() => {
    if (user && isAuthenticated) {
      import('./services/pusherService').then(({ pusherService }) => {
        pusherService.connect();
      });
      return () => {
        import('./services/pusherService').then(({ pusherService }) => {
          pusherService.disconnect();
        });
      };
    }
  }, [user, isAuthenticated]);

  // ステータスマスターを取得してキャッシュに設定
  const { reservationStatuses, vehicleStatuses, vehicleLocationStatuses, vehicleTypeStatuses } = useReservationStatuses();

  useEffect(() => {
    if (reservationStatuses && Object.keys(reservationStatuses).length > 0) {
      setStatusCache({
        reservation: reservationStatuses,
        vehicle: vehicleStatuses,
        vehicle_location: vehicleLocationStatuses,
        vehicle_type: vehicleTypeStatuses
      });
    }
  }, [reservationStatuses, vehicleStatuses, vehicleLocationStatuses, vehicleTypeStatuses]);

  // ブラウザバック・ページ離脱防止（全ページ共通）
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const syncHeaderHeight = () => {
      const el = headerRef.current;
      // If headerRef is not attached yet (e.g. loading), try to find by tag or wait
      // But here we only render AppBar when authenticated.
      if (!el) {
        // Fallback: if not found, maybe set a default or retry?
        // For now, let's set a reasonable default if not found, or 0.
        // Actually, if we are on a page where header is present, it should be found.
        return;
      }
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const mt = parseFloat(style.marginTop || '0');
      const mb = parseFloat(style.marginBottom || '0');
      const total = Math.round(rect.height + mt + mb);
      document.documentElement.style.setProperty('--app-header-height', `${total}px`);
    };

    // Run initially and on resize
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);

    // Also run after a short delay to ensure rendering is complete
    const timer = setTimeout(syncHeaderHeight, 100);

    return () => {
      window.removeEventListener('resize', syncHeaderHeight);
      clearTimeout(timer);
    };
  }, [user, isAuthenticated, isMobile]); // Re-run when auth state or mobile state changes (header might change height)

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const location = useLocation();

  // Mobile Redirect Logic
  useEffect(() => {
    if (isAuthenticated && isMobile && (
      location.pathname === '/' || location.pathname === '/calendar' ||
      location.pathname.startsWith('/calendar/') ||
      location.pathname === '/daylist' || location.pathname === '/vehicle_list' ||
      location.pathname === '/reservations'
    )) {
      navigate('/mobile');
    }
  }, [isAuthenticated, isMobile, navigate, location.pathname]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          認証確認中...
        </Typography>
      </Box>
    );
  }

  if (user && isAuthenticated) {
    const isDashboardLayout = location.pathname === '/dashboard' || location.pathname === '/stock';
    const isMobileLayout = location.pathname === '/mobile';

    return (
      <ReservationDataProvider>
      <NotificationPermissionPrompt />
      {isMobileLayout ? (
        <React.Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
          <Routes>
            <Route path="/mobile" element={<MobilePage />} />
          </Routes>
        </React.Suspense>
      ) : isDashboardLayout ? (
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/stock" element={<StockCheck />} />
          <Route path="/ai_chat_review" element={<AiChatReview />} />
          <Route path="/ai_action_log" element={<AiActionLog />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }} key={`calendar-${renderKey}`}>
        <AppBar position="static" color="default" elevation={1} ref={headerRef} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                noWrap
                component="span"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
                onClick={() => handleNavigation('/dashboard')}
              >
                {user?.company_name || 'レンタカー予約カレンダーUI'}
              </Typography>
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={(e) => setCalendarMenuAnchor(e.currentTarget)}
                    aria-label="カレンダーバリエーション"
                    sx={{ mr: -0.5 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    anchorEl={calendarMenuAnchor}
                    open={!!calendarMenuAnchor}
                    onClose={() => setCalendarMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setCalendarMenuAnchor(null);
                        handleNavigation('/calendar/us-military');
                      }}
                    >
                      カレンダー（米軍）
                    </MenuItem>
                  </Menu>
                  <Button color="inherit" onClick={() => handleNavigation('/calendar')} startIcon={<CalendarMonthIcon />}>予約カレンダー</Button>
                </Box>
                <Button color="inherit" onClick={() => handleNavigation('/reservations')} startIcon={<FormatListBulletedIcon />}>予約一覧</Button>
                <Button color="inherit" onClick={() => handleNavigation('/daylist')} startIcon={<ListAltIcon />}>貸出返却リスト</Button>
                <Button color="inherit" onClick={() => handleNavigation('/vehicle_list')} startIcon={<DirectionsCarIcon />}>車両リスト</Button>
                <Button color="inherit" onClick={() => handleNavigation('/line')} startIcon={<ChatIcon />}>LINE</Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && <AgentFloat />}
              <StaffSelector user={user} />
              <ChangeHistoryIcon />
              <IconButton color="error" onClick={() => {
                logout();
              }} size="small">
                <LogoutIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        <SessionExpiryWarning />
        <PusherToast />

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
        >
          <Box sx={{ width: 250 }} role="presentation">
            <List>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/calendar')}>
                <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
                <ListItemText primary="予約カレンダー" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/reservations')}>
                <ListItemIcon><FormatListBulletedIcon /></ListItemIcon>
                <ListItemText primary="予約一覧" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/daylist')}>
                <ListItemIcon><ListAltIcon /></ListItemIcon>
                <ListItemText primary="貸出返却リスト" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/vehicle_list')}>
                <ListItemIcon><DirectionsCarIcon /></ListItemIcon>
                <ListItemText primary="車両リスト" />
              </ListItem>
              <ListItem {...{button: true} as any} onClick={() => handleNavigation('/line')}>
                <ListItemIcon><ChatIcon /></ListItemIcon>
                <ListItemText primary="LINE" />
              </ListItem>
            </List>
            <Divider />
            <List>
              <ListItem>
                <ListItemText
                  primary={user?.username}
                  secondary={useMockAuth ? '📱 モック認証' : '🔗 PHP認証'}
                />
              </ListItem>
            </List>
          </Box>
        </Drawer>

        <StaffChatManager />
        {(location.pathname === '/line' || location.pathname === '/daylist') && <RecentSummaryFloat />}
        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/calendar" element={<CalendarV2 />} />
            <Route path="/calendar/us-military" element={<CalendarV2 vehicleStatusFilterId={6} />} />
            <Route path="/reservations" element={<ReservationList />} />
            <Route path="/daylist" element={<DayListReservations />} />
            <Route path="/vehicle_list" element={<VehicleList />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/line" element={<LineChatBoard />} />
            <Route path="/stock" element={<StockCheck />} />
            <Route path="/main_send" element={<BulkMail />} />
            <Route path="/payment_check" element={<PaymentCheck />} />
            <Route path="/ai_chat_review" element={<AiChatReview />} />
            <Route path="/ai_action_log" element={<AiActionLog />} />
          </Routes>
        </Box>
      </Box>
      )}
      {/* ソフトログアウト: 現在のページに滞在したままログインモーダル */}
      {softLoggedOut && createPortal(
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'none',
        }}>
          <Box sx={{
            background: '#fff', borderRadius: 3, p: 0,
            width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}>
            <Box sx={{ p: 3, pb: 1, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                セッションが終了しました
              </Typography>
              <Typography variant="body2" color="text.secondary">
                再度ログインしてください。現在のページは維持されます。
              </Typography>
            </Box>
            <LoginForm
              key={`soft-login-${renderKey}`}
              embedded
              onSuccess={() => {
                // LOGIN_SUCCESS で softLoggedOut: false になる
                // データを再読み込み
                import('./services/reservationSyncService').then(({ reservationSyncService }) => {
                  reservationSyncService.forceSync();
                });
                import('./services/vehicleSyncService').then(({ vehicleSyncService }) => {
                  vehicleSyncService.forceSync();
                });
              }}
            />
          </Box>
        </Box>,
        document.body
      )}
      </ReservationDataProvider>
    );
  }

  return <LoginForm key={`login-${renderKey}`} />;
}

export default App;
